import { useState, useEffect, useContext } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useCountUp } from 'react-countup'
import { useDispatch, useSelector } from 'react-redux'
import { Waypoint } from 'react-waypoint'
import moment from 'moment'
import {
  AmplitudeContext,
  AmplitudeEventConstants,
} from '@homebot/shared-util-quartermaster'

// Actions
import {
  fetchHomeInsurance,
  setHomeInsuranceOverride,
} from '../../../actions/homeInsurance'

// Constants
import { HOME_INSURANCE_RISKS } from '../../../constants/homeInsurance'
import { OVERLAY_HELPERS } from '../../../constants/navigation'
import {
  ScrolledIntoView,
  UpdateYourPolicyClick,
  MoreDetailsOnThisClick,
  EVENTS_LABELS,
} from '../../../constants/trackingEvents'

// Utils
import { oneTimeTagEventDispatcher } from '../../../helpers/tagManager'
import history from '../../../history'
import { useAmplitudeDebounceSetters } from '../../../hooks/tracking'

// Selectors
import { selectShouldRenderHomeInsuranceModule } from '../../../store/selectors/display'
import { selectHomeUuid } from '../../../store/selectors/home'
import { selectCustomerIsInsuranceAgent } from '../../../store/selectors/customerProfile'
import {
  selectClientHasConfirmedInsurancePolicyStartDate,
  selectCurrentRisk,
  selectCurrentRiskPercent,
  selectDaysUntilRenewal,
  selectInsuranceModuleOverride,
  selectRiskYearsSince,
} from '../../../store/selectors/homeInsurance'

// Components
import {
  AsyncPanel,
  Button,
  If,
  OverlayPanel,
  RadialMeter,
  Text,
  Unless,
} from '../..'
import YourInsurance from './YourInsurance'
import MoreDetails from './MoreDetails'
import RiskMessage from './RiskMessage'
import FlipDisplay from './FlipDisplay'

// Styles
import styles from './HomeInsurance.module.scss'

const HomeInsurance = () => {
  const dispatch = useDispatch()

  const intl = useIntl()

  const { ampli } = useContext(AmplitudeContext)

  const homeUuid = useSelector(selectHomeUuid)

  const clientHasConfirmedPolicyDate = useSelector(
    selectClientHasConfirmedInsurancePolicyStartDate
  )
  const displayInsuranceModuleOverride = useSelector(
    selectInsuranceModuleOverride
  )
  const shouldRenderHomeInsurance = useSelector(
    selectShouldRenderHomeInsuranceModule
  )

  const daysUntilRenewal = useSelector(selectDaysUntilRenewal)
  const insuranceAgentCustomerType = useSelector(selectCustomerIsInsuranceAgent)
  const percent = useSelector(selectCurrentRiskPercent)
  const risk = useSelector(selectCurrentRisk)
  const years = useSelector(selectRiskYearsSince)

  const { countUp: radialCountUp, update: updateRadial } = useCountUp({
    start: 0,
    end: 0,
    delay: 2500,
    duration: 3,
  })

  const { countUp: flipCountUp, update: updateFlip } = useCountUp({
    start: 0,
    end: 0,
    delay: 2500,
    duration: 3,
  })

  const [useOriginPath, setUseOriginPath] = useState(false)
  const originPath = OVERLAY_HELPERS.homeInsurance.getOrigin()
  const moreDetailsPath =
    OVERLAY_HELPERS.homeInsurance.buildPath('more-details')

  const handleNavToPanel = ({ path }) => {
    setUseOriginPath(true)
    history.push(OVERLAY_HELPERS.homeInsurance.buildPath(path))
  }

  const [trackModuleViewed] = useAmplitudeDebounceSetters(() =>
    ampli?.moduleViewed({ module_name: AmplitudeEventConstants.HOME_INSURANCE })
  )

  const trackInsuranceRiskViewed = () => {
    ampli?.insuranceRiskViewed({
      risk_level:
        risk === HOME_INSURANCE_RISKS.LOW
          ? AmplitudeEventConstants.LOW_INSURANCE_RISK
          : risk === HOME_INSURANCE_RISKS.MID
          ? AmplitudeEventConstants.MEDIUM_INSURANCE_RISK
          : AmplitudeEventConstants.HIGH_INSURANCE_RISK,
    })
  }

  const handleScrollIntoView = () => {
    oneTimeTagEventDispatcher(ScrolledIntoView, {
      eventLabel: EVENTS_LABELS.HomeInsuranceModule,
    })
    trackModuleViewed()
    trackInsuranceRiskViewed()
    updateRadial(percent)
    updateFlip(daysUntilRenewal)
  }

  useEffect(() => {
    if (homeUuid) {
      dispatch(fetchHomeInsurance(homeUuid))
    }
  }, [homeUuid, dispatch])

  useEffect(() => {
    const setOverride = () =>
      dispatch(setHomeInsuranceOverride({ override: false }))

    window.addEventListener('load', setOverride)

    return () => {
      window.removeEventListener('load', setOverride)
    }
  }, [dispatch])

  const showPanel = displayInsuranceModuleOverride || shouldRenderHomeInsurance

  return (
    <>
      <OverlayPanel
        back={originPath}
        path={moreDetailsPath}
        title={OVERLAY_HELPERS.getTitle('moreDetails', intl.formatMessage)}
        component={MoreDetails}
      />
      <OverlayPanel
        back={useOriginPath ? originPath : moreDetailsPath}
        path={OVERLAY_HELPERS.homeInsurance.buildPath('your-insurance')}
        title={OVERLAY_HELPERS.getTitle('yourInsurance', intl.formatMessage)}
        component={YourInsurance}
      />
      <AsyncPanel show={showPanel} className={styles.panel}>
        <If condition={!clientHasConfirmedPolicyDate}>
          <If
            condition={
              insuranceAgentCustomerType && risk === HOME_INSURANCE_RISKS.LOW
            }
          >
            <Text
              data-qa='homeowners-insurance-module'
              className={styles.title}
              size='xl'
              weight='light'
              variant='neutralCool'
              variantCount='900'
            >
              <FormattedMessage
                id='Homeowner.homeInsurance.module.title.lowRisk'
                defaultMessage='Looks like you last reviewed your policy in {year}'
                values={{
                  year: moment().subtract(years, 'years').year(),
                }}
              />
            </Text>
          </If>
          <Unless
            condition={
              insuranceAgentCustomerType && risk === HOME_INSURANCE_RISKS.LOW
            }
          >
            <Text
              data-qa='homeowners-insurance-module'
              className={styles.title}
              size='xl'
              weight='light'
              variant='neutralCool'
              variantCount='900'
            >
              <FormattedMessage
                id='Homeowner.homeInsurance.module.title'
                defaultMessage='Have you done a home insurance checkup since {year}?'
                values={{
                  year: moment().subtract(years, 'years').year(),
                }}
              />
            </Text>
          </Unless>
          <If condition={!insuranceAgentCustomerType}>
            <Text
              className={styles.titleSub}
              variant='neutralCool'
              variantCount='700'
            >
              <FormattedMessage
                id='Homeowner.homeInsurance.module.title.sub'
                defaultMessage='Your risk of accidentally overpaying or being underinsured is {risk}'
                values={{
                  risk: !!risk && <RiskMessage risk={risk} />,
                }}
              />
            </Text>
          </If>

          <If condition={insuranceAgentCustomerType}>
            <If condition={risk === HOME_INSURANCE_RISKS.HIGH}>
              <Text
                className={styles.titleSub}
                variant='neutralCool'
                variantCount='700'
              >
                <FormattedMessage
                  id='Homeowner.homeInsurance.module.title.sub.alt.high'
                  defaultMessage='It’s likely time to ensure you’re adequately covered and your rates are still competitive'
                />
              </Text>
            </If>
            <If condition={risk === HOME_INSURANCE_RISKS.MID}>
              <Text
                className={styles.titleSub}
                variant='neutralCool'
                variantCount='700'
              >
                <FormattedMessage
                  id='Homeowner.homeInsurance.module.title.sub.alt.med'
                  defaultMessage='You might want to evaluate your coverage to ensure your rates are still competitive in the marketplace'
                />
              </Text>
            </If>
          </If>
        </If>
        {showPanel && (
          <Waypoint onEnter={handleScrollIntoView} bottomOffset='50%' />
        )}
        <If condition={clientHasConfirmedPolicyDate}>
          <If condition={!insuranceAgentCustomerType}>
            <Text
              data-qa='homeowners-insurance-module'
              className={styles.title}
              size='xl'
              weight='light'
              variant='neutralCool'
              variantCount='900'
            >
              <FormattedMessage
                id='Homeowner.homeInsurance.insuranceCheckup'
                defaultMessage='Nice! You did a home insurance checkup!'
              />
            </Text>
            <Text
              className={styles.titleSub}
              variant='neutralCool'
              variantCount='700'
            >
              <FormattedMessage
                id='Homeowner.homeInsurance.insuranceCheckupSub'
                defaultMessage='Now, your risk of accidentally overpaying or being underinsured is {risk}'
                values={{
                  risk: !!risk && <RiskMessage risk={risk} />,
                }}
              />
            </Text>
          </If>
          <If condition={insuranceAgentCustomerType}>
            <Text
              className={styles.titleSub}
              variant='neutralCool'
              variantCount='700'
            >
              <FormattedMessage
                id='Homeowner.homeInsurance.insuranceCheckup.alt'
                defaultMessage='Nice, looks like you recently reviewed your home insurance!'
              />
            </Text>
          </If>
        </If>
        <RadialMeter className={styles.riskMeter} percent={radialCountUp} />
        <div className={styles.daysWrapper}>
          <FlipDisplay
            className={styles.daysFlip}
            value={flipCountUp || ''}
            fixedLength={daysUntilRenewal && daysUntilRenewal.length}
          >
            <Text
              upperspaced
              size='xxs'
              weight='semibold'
              variant='neutralCool'
              variantCount='900'
            >
              <FormattedMessage
                id='Homeowner.homeInsurance.module.days'
                defaultMessage='days'
              />
            </Text>
          </FlipDisplay>
          <Text
            size='l'
            variant='neutralCool'
            variantCount='700'
            className={styles.daysTitle}
          >
            <FormattedMessage
              id='Homeowner.homeInsurance.module.days.explain'
              defaultMessage='Until your home insurance might automatically renew'
            />
          </Text>
          <Text
            size='s'
            variant='neutralCool'
            variantCount='700'
            weight='light'
            className={styles.daysSub}
          >
            <FormattedMessage
              id='Homeowner.homeInsurance.module.days.ctaExplain'
              defaultMessage='Take a few minutes to review your policy'
            />
          </Text>
          <Button
            className={styles.ctaButton}
            data-qa='nav-hi-panel' // TODO: revisit this data qa for E2E
            track={MoreDetailsOnThisClick}
            trackOptions={{ eventLabel: EVENTS_LABELS.HomeInsuranceModule }}
            onClick={() => {
              ampli?.insuranceReviewed({
                insurance_policy_checked:
                  AmplitudeEventConstants.MORE_INFO_ON_HOME_INSURANCE,
              })
              handleNavToPanel({ path: 'more-details' })
            }}
          >
            <FormattedMessage
              id='Homeowner.homeInsurance.moreDetailsBtn'
              defaultMessage='More information on this'
            />
          </Button>
          <Unless condition={clientHasConfirmedPolicyDate}>
            <Button
              className={styles.ctaButton}
              data-qa='nav-your-insurance'
              track={UpdateYourPolicyClick}
              trackOptions={{ eventLabel: EVENTS_LABELS.HomeInsuranceModule }}
              onClick={() => {
                ampli?.insuranceReviewed({
                  insurance_policy_checked:
                    AmplitudeEventConstants.UPDATE_HOME_INSURANCE_POLICY,
                })
                handleNavToPanel({ path: 'your-insurance' })
              }}
            >
              <FormattedMessage
                id='Homeowner.homeInsurance.updateYourPolicyDetailsBtn'
                defaultMessage='Update your policy details'
              />
            </Button>
          </Unless>
        </div>
      </AsyncPanel>
    </>
  )
}

export default HomeInsurance
